import { useEffect, useState } from 'react';
import { listAppSkills } from '@/features/apps/apps-client';
import { usePluginRevision } from '@/features/plugins/use-plugin-revision';
import { collectPluginSkills } from '@/lib/native-agent/session-meta';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import { nativePluginClient, onPluginsChanged } from '@/lib/plugins/plugin-native-client';

/**
 * Lists skill resources for the composer: the plugins' and the apps'. Their content stays behind
 * the MCP servers that serve them to agents.
 */
export function useAgentPluginSkills(input: { enabled: boolean; workspaceRoot: string | null }): {
  availableSkills: readonly AvailablePluginSkill[];
  loading: boolean;
} {
  const pluginRevision = usePluginRevision();
  const [availableSkills, setAvailableSkills] = useState<readonly AvailablePluginSkill[]>([]);
  const key = input.enabled ? (input.workspaceRoot ?? '') : null;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [appsRevision, setAppsRevision] = useState(0);
  useEffect(() => onPluginsChanged(() => setAppsRevision((value) => value + 1)), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies(pluginRevision): lists the skills again when the plugins change.
  // biome-ignore lint/correctness/useExhaustiveDependencies(appsRevision): lists the skills again when the apps change.
  useEffect(() => {
    if (!input.enabled) {
      setAvailableSkills([]);
      setLoadedKey(null);
      return;
    }
    let cancelled = false;
    void Promise.all([
      nativePluginClient.list(input.workspaceRoot ?? undefined).then(collectPluginSkills),
      listAppSkills().catch(() => []),
    ])
      .then(([plugins, apps]) => {
        const available = [...plugins, ...apps];
        if (!cancelled) {
          setAvailableSkills(available);
          setLoadedKey(key);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableSkills([]);
          setLoadedKey(key);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input.enabled, input.workspaceRoot, key, pluginRevision, appsRevision]);

  return { availableSkills, loading: key !== null && loadedKey !== key };
}
